/* Musical Intelligence Resource Library — Owner UI (Milestone 2) */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import {
  Library, Book, FileText, GitMerge, Upload, ShieldAlert,
  BarChart3, Download, RefreshCw, Loader2, CheckCircle2, XCircle,
  AlertCircle, Play, Sprout,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "dashboard",   label: "Dashboard",         icon: BarChart3 },
  { key: "works",       label: "Canonical Works",   icon: Library },
  { key: "resources",   label: "Resources",         icon: Book },
  { key: "entries",     label: "Resource Entries",  icon: FileText },
  { key: "duplicates",  label: "Duplicate Review",  icon: GitMerge },
  { key: "imports",     label: "Imports",           icon: Upload },
  { key: "rights",      label: "Rights Review",     icon: ShieldAlert },
];

function Card({ label, value, tone = "neutral", testid }) {
  const toneBg = { neutral: "#f3f4f6", warn: "#fef3c7", danger: "#fee2e2",
                    ok: "#dcfce7" }[tone] || "#f3f4f6";
  const toneFg = { neutral: "#111827", warn: "#78350f", danger: "#991b1b",
                    ok: "#166534" }[tone] || "#111827";
  return (
    <div data-testid={testid}
         style={{ padding: 16, background: toneBg, borderRadius: 8,
                   minWidth: 160 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase",
                      letterSpacing: 0.8, color: toneFg, opacity: 0.7 }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 28, fontWeight: 700,
                      color: toneFg, fontVariantNumeric: "tabular-nums" }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/repertoire/summary`);
      setSummary(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const seedEpiscopal = async () => {
    setBusy(true);
    setSeedResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/repertoire/seed-episcopal`, {});
      setSeedResult(data);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(
        `${API}/admin/repertoire/imports/template.csv`,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "repertoire_import_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="rep-dashboard">
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Library Dashboard</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="rep-refresh-btn" onClick={refresh}
                  className="btn-secondary"
                  style={{ display: "inline-flex", alignItems: "center",
                            gap: 6, padding: "6px 12px" }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="rep-download-template-btn"
                  onClick={downloadTemplate} className="btn-secondary"
                  style={{ display: "inline-flex", alignItems: "center",
                            gap: 6, padding: "6px 12px" }}>
            <Download size={14} /> Download CSV Template
          </button>
          <button data-testid="rep-seed-episcopal-btn" onClick={seedEpiscopal}
                  disabled={busy} className="btn-primary"
                  style={{ display: "inline-flex", alignItems: "center",
                            gap: 6, padding: "6px 12px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <Sprout size={14} />}
            Load Episcopal Starter Resources
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12,
                     fontStyle: "italic" }}>
        Creates or updates four Episcopal publication records as an initial dataset.
        This does not define or restrict the universal repertoire catalog.
      </div>
      {error && (
        <div data-testid="rep-error"
             style={{ padding: 8, background: "#fee2e2", color: "#991b1b",
                       borderRadius: 4, marginBottom: 12, fontSize: 12 }}>
          <AlertCircle size={14} style={{ verticalAlign: "middle",
                                            marginRight: 6 }} />
          {error}
        </div>
      )}
      {seedResult && (
        <div data-testid="rep-seed-result"
             style={{ padding: 8, background: "#dcfce7", color: "#166534",
                       borderRadius: 4, marginBottom: 12, fontSize: 12 }}>
          <CheckCircle2 size={14} style={{ verticalAlign: "middle",
                                             marginRight: 6 }} />
          Seed idempotent: {seedResult.seeded} new resources,
          {" "}{seedResult.left_alone} already present.
        </div>
      )}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Card testid="rep-card-works" label="Canonical Works"
              value={summary?.works_total} />
        <Card testid="rep-card-resources" label="Resources"
              value={summary?.resources_total} />
        <Card testid="rep-card-entries" label="Resource Entries"
              value={summary?.resource_entries_total} />
        <Card testid="rep-card-pending-dupes" label="Pending Duplicates"
              value={summary?.pending_duplicates}
              tone={summary?.pending_duplicates > 0 ? "warn" : "neutral"} />
        <Card testid="rep-card-pending-rights" label="Pending Rights Review"
              value={summary?.pending_rights_review}
              tone={summary?.pending_rights_review > 0 ? "warn" : "neutral"} />
        <Card testid="rep-card-unmatched-imports" label="Unmatched Import Rows"
              value={summary?.pending_unmatched_import_rows}
              tone={summary?.pending_unmatched_import_rows > 0 ? "warn" : "neutral"} />
      </div>
      {summary?.latest_import_batch && (
        <div data-testid="rep-latest-batch"
             style={{ marginTop: 20, padding: 12, background: "#f9fafb",
                       borderRadius: 6, border: "1px solid #e5e7eb" }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Latest Import Batch
          </div>
          <div style={{ fontSize: 12, color: "#4b5563",
                          fontFamily: "ui-monospace, monospace" }}>
            <div><b>ID:</b> {summary.latest_import_batch.id}</div>
            <div><b>Filename:</b> {summary.latest_import_batch.filename}</div>
            <div><b>Status:</b> {summary.latest_import_batch.status}</div>
            <div><b>Rows created:</b>{" "}
              {summary.latest_import_batch.rows_created} /{" "}
              {summary.latest_import_batch.rows_total}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResourcesTab() {
  const [resources, setResources] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/repertoire/resources`)
      .then((r) => setResources(r.data.resources || []))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);
  return (
    <div data-testid="rep-resources-tab">
      <h2 style={{ marginTop: 0 }}>Resources</h2>
      {error && <div className="rep-err">{error}</div>}
      <table style={{ width: "100%", fontSize: 12,
                        borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f9fafb", textAlign: "left" }}>
            <th style={{ padding: 8 }}>ID</th>
            <th style={{ padding: 8 }}>Short</th>
            <th style={{ padding: 8 }}>Title</th>
            <th style={{ padding: 8 }}>Traditions</th>
            <th style={{ padding: 8 }}>Rights</th>
          </tr>
        </thead>
        <tbody>
          {resources.map((r) => (
            <tr key={r.id} data-testid={`rep-resource-${r.id}`}
                style={{ borderTop: "1px solid #e5e7eb" }}>
              <td style={{ padding: 8, fontFamily: "monospace" }}>{r.id}</td>
              <td style={{ padding: 8 }}>{r.short_title || "—"}</td>
              <td style={{ padding: 8 }}>{r.title}</td>
              <td style={{ padding: 8 }}>{(r.traditions || []).join(", ")}</td>
              <td style={{ padding: 8 }}>
                <RightsBadge rights={r.rights} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {resources.length === 0 && (
        <div data-testid="rep-resources-empty"
             style={{ padding: 24, textAlign: "center", color: "#6b7280",
                       fontSize: 13 }}>
          No resources yet. Use "Seed Episcopal Resources" on the Dashboard tab
          to create the four owner-approved Episcopal source resources.
        </div>
      )}
    </div>
  );
}

function RightsBadge({ rights }) {
  const badge = rights?.badge || "RIGHTS_UNKNOWN";
  const tone = {
    PUBLIC_DOMAIN:     { bg: "#dcfce7", fg: "#166534" },
    LICENSED:          { bg: "#e0f2fe", fg: "#075985" },
    PARISH_SUPPLIED:   { bg: "#fef3c7", fg: "#78350f" },
    METADATA_ONLY:     { bg: "#f3f4f6", fg: "#374151" },
    RIGHTS_UNKNOWN:    { bg: "#fef3c7", fg: "#78350f" },
    PENDING_RIGHTS_REVIEW: { bg: "#fee2e2", fg: "#991b1b" },
    REJECTED:          { bg: "#fee2e2", fg: "#991b1b" },
  }[badge] || { bg: "#f3f4f6", fg: "#374151" };
  return (
    <span data-testid={`rep-rights-${badge}`}
          style={{ padding: "2px 6px", borderRadius: 4,
                     background: tone.bg, color: tone.fg,
                     fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
      {badge.replace(/_/g, " ")}
    </span>
  );
}

function ImportsTab() {
  const [file, setFile] = useState(null);
  const [resourceId, setResourceId] = useState("");
  const [resources, setResources] = useState([]);
  const [dryRun, setDryRun] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/repertoire/resources`)
      .then((r) => setResources(r.data.resources || []));
  }, []);

  const runDryRun = async () => {
    setBusy(true); setError(null); setDryRun(null); setCommitted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${API}/admin/repertoire/imports/dry-run` +
        (resourceId ? `?resource_id=${resourceId}` : "");
      const { data } = await axios.post(url, fd);
      setDryRun(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${API}/admin/repertoire/imports` +
        (resourceId ? `?resource_id=${resourceId}` : "");
      const { data } = await axios.post(url, fd);
      setCommitted(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const rollback = async (batchId) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/repertoire/imports/${batchId}/rollback`, {});
      setCommitted({ ...committed, status: "rolled_back",
                       rollback_result: data });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="rep-imports-tab">
      <h2 style={{ marginTop: 0 }}>Imports</h2>
      <div style={{ display: "flex", gap: 8, alignItems: "center",
                      marginBottom: 12, flexWrap: "wrap" }}>
        <input type="file" accept=".csv"
                data-testid="rep-import-file"
                onChange={(e) => setFile(e.target.files[0])} />
        <select data-testid="rep-import-resource"
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}>
          <option value="">— Select target resource (optional) —</option>
          {resources.map((r) => (
            <option key={r.id} value={r.id}>
              {r.short_title || r.id} — {r.title}
            </option>
          ))}
        </select>
        <button data-testid="rep-import-dry-run" onClick={runDryRun}
                disabled={!file || busy} className="btn-secondary"
                style={{ padding: "6px 12px" }}>
          {busy ? <Loader2 size={14} className="spin" /> : <Play size={14} />}
          {" "}Dry Run
        </button>
        <button data-testid="rep-import-commit" onClick={commit}
                disabled={!file || busy || !dryRun} className="btn-primary"
                style={{ padding: "6px 12px" }}>
          {busy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
          {" "}Commit
        </button>
      </div>
      {error && <div className="rep-err">{error}</div>}
      {dryRun && (
        <div data-testid="rep-dry-run-report"
             style={{ padding: 12, background: "#f9fafb", borderRadius: 6,
                       marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Dry-Run Report
          </div>
          <div style={{ fontSize: 12 }}>
            Rows total: <b data-testid="rep-dry-total">{dryRun.rows_total}</b>
            {" · "}Would create: <b>{dryRun.would_create}</b>
            {" · "}Would match existing: <b>{dryRun.would_match_existing}</b>
            {" · "}Rejected: <b>{dryRun.rows_rejected}</b>
          </div>
          {(dryRun.issues || []).length > 0 && (
            <ul style={{ marginTop: 8, fontSize: 11 }}>
              {dryRun.issues.slice(0, 30).map((i, idx) => (
                <li key={idx} data-testid={`rep-dry-issue-${idx}`}>
                  {i.row ? `Row ${i.row}: ` : ""}{i.error || i.warning}
                  {i.match_method && ` (${i.match_method}, conf ${i.confidence?.toFixed?.(2)})`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {committed && (
        <div data-testid="rep-commit-report"
             style={{ padding: 12, background: "#dcfce7", borderRadius: 6,
                       borderLeft: "3px solid #059669" }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: "#166534" }}>
            Import Committed — batch {committed.id}
          </div>
          <div style={{ fontSize: 12 }}>
            Status: <b>{committed.status}</b>
            {" · "}Created: <b>{committed.rows_created}</b>
            {" · "}Rejected: <b>{committed.rows_rejected}</b>
          </div>
          {committed.status !== "rolled_back" && (
            <button data-testid="rep-commit-rollback"
                    onClick={() => rollback(committed.id)}
                    style={{ marginTop: 8, padding: "4px 8px",
                              background: "#fee2e2", color: "#991b1b",
                              border: "1px solid #fca5a5", borderRadius: 4,
                              fontSize: 11, cursor: "pointer" }}>
              Rollback
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ListTab({ endpoint, testid, columns, emptyText }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}${endpoint}`)
      .then((r) => {
        const key = Object.keys(r.data).find((k) => Array.isArray(r.data[k]));
        setItems(key ? r.data[key] : []);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [endpoint]);
  return (
    <div data-testid={testid}>
      {error && <div className="rep-err">{error}</div>}
      {items.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280",
                        fontSize: 13 }}>
          {emptyText}
        </div>
      )}
      {items.length > 0 && (
        <table style={{ width: "100%", fontSize: 12,
                          borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb", textAlign: "left" }}>
              {columns.map((c) => (
                <th key={c.key} style={{ padding: 8 }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}
                  data-testid={`${testid}-row-${it.id}`}
                  style={{ borderTop: "1px solid #e5e7eb" }}>
                {columns.map((c) => (
                  <td key={c.key} style={{ padding: 8,
                                            fontFamily: c.mono ? "monospace" : undefined }}>
                    {c.render ? c.render(it) : (it[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RightsReviewTab() {
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const refresh = useCallback(() => {
    axios.get(`${API}/admin/repertoire/rights-review`)
      .then((r) => setEntries(r.data.entries || []))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const decide = async (entryId, decision) => {
    try {
      await axios.post(
        `${API}/admin/repertoire/rights-review/${entryId}/decision`,
        { decision });
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };
  return (
    <div data-testid="rep-rights-tab">
      <h2 style={{ marginTop: 0 }}>Rights Review Queue</h2>
      {error && <div className="rep-err">{error}</div>}
      {entries.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280",
                        fontSize: 13 }}>
          No entries currently pending review.
        </div>
      )}
      {entries.map((e) => (
        <div key={e.id} data-testid={`rep-rights-entry-${e.id}`}
             style={{ padding: 8, marginBottom: 6, background: "#fff7ed",
                       borderRadius: 4, border: "1px solid #fdba74",
                       fontSize: 12 }}>
          <div>
            <b>{e.resource_title || e.canonical_work_id}</b> ·{" "}
            resource={e.resource_id} · number={e.resource_number}
          </div>
          <div style={{ fontSize: 11, color: "#7c2d12", marginTop: 4 }}>
            Rights: {e.rights?.badge} · legal_basis: {e.rights?.legal_basis || "—"}
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
            <button onClick={() => decide(e.id, "approve")}
                    data-testid={`rep-rights-approve-${e.id}`}
                    style={{ padding: "3px 8px", background: "#dcfce7",
                              color: "#166534", border: "1px solid #86efac",
                              borderRadius: 3, fontSize: 11, cursor: "pointer" }}>
              Approve
            </button>
            <button onClick={() => decide(e.id, "reject")}
                    data-testid={`rep-rights-reject-${e.id}`}
                    style={{ padding: "3px 8px", background: "#fee2e2",
                              color: "#991b1b", border: "1px solid #fca5a5",
                              borderRadius: 3, fontSize: 11, cursor: "pointer" }}>
              Reject
            </button>
            <button onClick={() => decide(e.id, "return_for_info")}
                    data-testid={`rep-rights-return-${e.id}`}
                    style={{ padding: "3px 8px", background: "#fef3c7",
                              color: "#78350f", border: "1px solid #fcd34d",
                              borderRadius: 3, fontSize: 11, cursor: "pointer" }}>
              Return for info
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminRepertoire() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (!user) return <div style={{ padding: 24 }}>Sign in required.</div>;

  return (
    <div data-testid="admin-repertoire-root"
         style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <style>{`
        .rep-err { padding: 8px; background: #fee2e2; color: #991b1b;
                    border-radius: 4px; margin-bottom: 12px; font-size: 12px; }
        .btn-primary { background: #4f46e5; color: white; border: none;
                        border-radius: 4px; cursor: pointer; font-size: 12px; }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { background: white; color: #374151; border: 1px solid #d1d5db;
                          border-radius: 4px; cursor: pointer; font-size: 12px; }
        .rep-tab { padding: 8px 12px; background: transparent; border: none;
                    cursor: pointer; color: #4b5563; font-size: 13px;
                    display: inline-flex; align-items: center; gap: 6px;
                    border-bottom: 2px solid transparent; }
        .rep-tab.active { color: #4f46e5; border-bottom-color: #4f46e5;
                           font-weight: 600; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <h1 style={{ marginTop: 0 }}>Musical Intelligence — Resource Library</h1>
      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>
        Owner-only catalog for canonical works, resources, resource entries,
        and rights review. Signed in as <b>{user?.email}</b>.
      </div>
      <div style={{ borderBottom: "1px solid #e5e7eb", marginBottom: 20 }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key}
                  data-testid={`rep-tab-${key}`}
                  className={`rep-tab ${tab === key ? "active" : ""}`}
                  onClick={() => setTab(key)}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard  />}
      {tab === "resources" && <ResourcesTab  />}
      {tab === "imports"   && <ImportsTab  />}
      {tab === "rights"    && <RightsReviewTab  />}
      {tab === "works" && (
        <ListTab endpoint="/repertoire/works" testid="rep-works-tab"
                  
                  emptyText="No canonical works yet. Import a CSV to populate the catalog."
                  columns={[
                    { key: "id", label: "ID", mono: true },
                    { key: "canonical_title", label: "Title" },
                    { key: "tune_name", label: "Tune" },
                    { key: "meter", label: "Meter" },
                    { key: "text_author", label: "Author" },
                    { key: "composer", label: "Composer" },
                    { key: "rights", label: "Rights",
                      render: (w) => <RightsBadge rights={w.rights} /> },
                  ]} />
      )}
      {tab === "entries" && (
        <div data-testid="rep-entries-tab">
          <h2 style={{ marginTop: 0 }}>Resource Entries</h2>
          <div style={{ padding: 24, textAlign: "center", color: "#6b7280",
                          fontSize: 13 }}>
            Select a resource from the Resources tab to view its entries.
            Direct bulk-listing across all resources is deferred to M3.
          </div>
        </div>
      )}
      {tab === "duplicates" && (
        <div data-testid="rep-duplicates-tab">
          <h2 style={{ marginTop: 0 }}>Duplicate Review</h2>
          <div style={{ padding: 24, textAlign: "center", color: "#6b7280",
                          fontSize: 13 }}>
            Duplicate candidates surface on the Dashboard tab (see "Pending
            Duplicates" card) and inside the Imports tab's dry-run report.
            Full merge/reject UI ships in M3.
          </div>
        </div>
      )}
    </div>
  );
}
